import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import back_app from '../../lib/back_app';
import { cat_id2lbl } from '../../lib/cat';
import CreatorHeader from '../../components/creator/CreatorHeader';

import xdate from '../../../../../util/date.js';

type Video = {
  id: string;
  title: string;
  description: string;
  video_url?: string;
  thumbnail_url?: string;
  category: string;
  status: string;
  ts?: {insert: Date, modified: Date};
  duration: number;
  ratio?: string;
  campaigns_count?: number;
};

function VideoCard({ video, onDelete, onView, onPreview }: { 
  video: Video; 
  onDelete: (e: React.MouseEvent) => void;
  onView: () => void;
  onPreview: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ratio, setRatio] = useState('');
  const [duration, setDuration] = useState(0);

  const handleLoadedMetadata = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.duration && isFinite(el.duration)) setDuration(el.duration);
    if (el.videoWidth && el.videoHeight) {
      const gcd = (a: number, b: number): number => b ? gcd(b, a % b) : a;
      const g = gcd(el.videoWidth, el.videoHeight) || 1;
      setRatio(`${el.videoWidth / g}:${el.videoHeight / g}`);
    }
  };

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const ratio_lbl = video.ratio || ratio;
  const dur_sec = video.duration || duration;

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-lg transition-all">
      {}
      <div 
        className="relative bg-gradient-to-br from-gray-900 to-gray-700 cursor-pointer overflow-hidden group"
        style={{ aspectRatio: '9/16' }}
        onClick={onPreview}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {video.video_url ? (
          <>
            <video
              ref={videoRef}
              src={video.video_url}
              className="w-full h-full object-cover"
              muted
              loop
              preload="metadata"
              onLoadedMetadata={handleLoadedMetadata}
            />
            {}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
              <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600 ml-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                </svg>
              </div>
            </div>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-white text-5xl">🎬</div>
          </div>
        )}

        {}
        <div className="absolute top-3 right-3">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            video.status === 'published' ? 'bg-green-500 text-white' :
            video.status === 'processing' ? 'bg-yellow-500 text-white' :
            'bg-gray-500 text-white'
          }`}>
            {video.status}
          </span>
        </div>

        {}
        {video.campaigns_count !== undefined && video.campaigns_count > 0 && (
          <div className="absolute top-3 left-3">
            <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"/>
              </svg>
              {video.campaigns_count}
            </div>
          </div>
        )}

        {}
        {ratio_lbl && (
          <div className="absolute bottom-3 left-3 bg-black bg-opacity-75 px-2 py-1 rounded text-white text-xs font-semibold">
            {ratio_lbl}
          </div>
        )}

        {}
        {dur_sec > 0 && (
          <div className="absolute bottom-3 right-3 bg-black bg-opacity-75 px-2 py-1 rounded text-white text-xs font-semibold">
            {Math.floor(dur_sec / 60)}:{String(Math.floor(dur_sec % 60)).padStart(2, '0')}
          </div>
        )}
      </div>

      {}
      <div className="p-4">
        <h3
          className="font-bold text-gray-900 line-clamp-2 mb-2 cursor-pointer hover:text-blue-600 transition-colors min-h-[3rem]"
          onClick={onView}
        >
          {video.title}
        </h3>

        {video.category && (
          <div className="mb-3">
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
              {cat_id2lbl(video.category)}
            </span>
          </div>
        )}

        <div className="text-sm text-gray-600 mb-4">
          {video.ts?.insert ? xdate.fmt_lbl_short(video.ts.insert) : ''}
        </div>

        {}
        <div className="grid grid-cols-1 gap-2">
          <button
            onClick={onView}
            className="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg text-sm font-semibold hover:shadow-lg transition-all"
          >
            View Details
          </button>
          <button
            onClick={onDelete}
            className="w-full px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-50 transition-all"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function VideoLibrary() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [previewVideo, setPreviewVideo] = useState<Video | null>(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  useEffect(() => {
    if (!previewVideo) return;
    window.history.pushState({modal: 'preview_video'}, '');
    let popped = false;
    const on_pop = () => {
      popped = true;
      setPreviewVideo(null);
    };
    window.addEventListener('popstate', on_pop);
    return () => {
      window.removeEventListener('popstate', on_pop);
      if (!popped && window.history.state?.modal == 'preview_video')
        window.history.back();
    };
  }, [previewVideo]);

  const fetchVideos = async () => {
    try {
      const result = await back_app.video_list();

      if (!result.err) {
        const videosData = result || [];
        
        const videosWithCounts = await Promise.all(
          videosData.map(async (video: Video) => {
            try {
              const campaignsResult = await back_app.vcamp_list({video_id: video.id});
              return {
                ...video,
                campaigns_count: !campaignsResult.err ? (campaignsResult?.length || 0) : 0
              };
            } catch (error) {
              console.error(`Error fetching campaigns for video ${video.id}:`, error);
              return { ...video, campaigns_count: 0 };
            }
          })
        );
        
        setVideos(videosWithCounts);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (videoId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (!confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      const result = await back_app.video_delete(videoId);

      if (!result.err) {
        alert('Video deleted successfully');
        fetchVideos();
      } else {
        alert('Error deleting video');
      }
    } catch (error) {
      console.error('Error deleting video:', error);
      alert('Error deleting video');
    }
  };

  const filteredVideos = videos.filter(video => {
    const matchesSearch = video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         video.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || video.category === filterCategory;

    return matchesSearch && matchesCategory;
  });

  const categories = ['all', ...new Set(videos.map(v => v.category).filter(Boolean))];

  return (
    <div className="min-h-screen bg-gray-50">
      <CreatorHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">My Videos</h2>
            <p className="text-gray-600 mt-1">
              {filteredVideos.length} {filteredVideos.length === 1 ? 'video' : 'videos'}
            </p>
          </div>
          <button
            onClick={() => navigate('/creator/upload')}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap"
          >
            + Upload Video
          </button>
        </div>

        {}
        <div className="bg-white rounded-xl border p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Search videos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category === 'all' ? 'All Categories' : cat_id2lbl(category)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600 mt-4">Loading videos...</p>
          </div>
        ) : filteredVideos.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <div className="text-6xl mb-4">
              {searchTerm || filterCategory !== 'all' ? '🔍' : '🎬'}
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {searchTerm || filterCategory !== 'all' ? 'No videos found' : 'No videos yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || filterCategory !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Upload your first video to get started'}
            </p>
            {!searchTerm && filterCategory === 'all' && (
              <button
                onClick={() => navigate('/creator/upload')}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Upload First Video
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6">
            {filteredVideos.map((video) => (
              <VideoCard
                key={video.id}
                video={video}
                onDelete={(e) => handleDelete(video.id, e)}
                onView={() => navigate(`/creator/videos/${video.id}`)}
                onPreview={() => setPreviewVideo(video)}
              />
            ))}
          </div>
        )}
      </div>

      {}
      {previewVideo && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-start justify-center z-50 p-4 overflow-y-auto"
          onClick={() => setPreviewVideo(null)}
        >
          <div
            className="relative w-full max-w-md my-auto"
            onClick={e => e.stopPropagation()}
          >
            {}
            <button
              onClick={() => setPreviewVideo(null)}
              className="absolute top-2 right-2 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-1 z-10"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
              </svg>
            </button>

            {}
            <div
              className="bg-black rounded-xl overflow-hidden mx-auto"
              style={{aspectRatio: '9/16', maxHeight: '60vh',
                width: 'min(100%, calc(60vh * 9 / 16))'}}
            >
              <video
                src={previewVideo.video_url}
                controls
                autoPlay
                className="w-full h-full"
              />
            </div>

            {}
            <div className="mt-4 text-white">
              <h3 className="text-xl font-bold mb-2">{previewVideo.title}</h3>
              <div className="flex items-center gap-3 text-sm text-gray-300 mb-3">
                <span>{previewVideo.ts?.insert ? xdate.fmt_lbl_short(previewVideo.ts.insert) : ''}</span>
                {previewVideo.campaigns_count !== undefined && previewVideo.campaigns_count > 0 && (
                  <>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"/>
                      </svg>
                      <span>{previewVideo.campaigns_count} {previewVideo.campaigns_count === 1 ? 'campaign' : 'campaigns'}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {}
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => {
                  navigate(`/creator/videos/${previewVideo.id}`);
                  setPreviewVideo(null);
                }}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                View Details
              </button>
              <button
                onClick={() => setPreviewVideo(null)}
                className="px-6 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}